import { useCallback, useEffect, useRef, type KeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { PLAYBACK_LIMIT_SECONDS, type PlaybackQuotaKind } from "../library/playbackUsageStore.js";
import { PRO_COPY } from "../library/proCopy.js";
import { useUpgradePrompt } from "../library/upgradePrompt.js";
import { runRemoteAction } from "../remote/remoteActions.js";

const WHAT: Record<PlaybackQuotaKind, string> = {
  live: "live TV",
  movie: "movies",
  series: "series",
};

function allowance(kind: PlaybackQuotaKind): string {
  if (!Number.isFinite(PLAYBACK_LIMIT_SECONDS[kind])) return "time"; // unlimited kinds never run out
  const minutes = Math.round(PLAYBACK_LIMIT_SECONDS[kind] / 60);
  return minutes >= 60 && minutes % 60 === 0 ? `${minutes / 60} hours` : `${minutes} minutes`;
}

/**
 * Free viewing time is used up: ask to upgrade (owner, 2026-09-29), from any
 * screen (library/upgradePrompt.ts). It used to fail in silence.
 *
 * Focus starts on Upgrade and stays inside (what opens takes focus, owner
 * 2026-09-26); Back closes it like any other popup. Until Play Billing and
 * activation codes exist, Upgrade opens the licensing card in Settings, where
 * the APK's Free/Paid switch lives. That card is in test builds only; a release
 * build is Paid until payments exist (default_entitlement), so it never asks.
 */
export function UpgradePrompt() {
  const kind = useUpgradePrompt((s) => s.kind);
  const close = useUpgradePrompt((s) => s.close);
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  const upgradeRef = useRef<HTMLButtonElement>(null);
  const open = kind !== null;

  useDialogFocus({ open, onClose: close, rootRef: dialogRef });

  useEffect(() => {
    if (open) upgradeRef.current?.focus();
  }, [open]);

  const upgrade = useCallback(() => {
    close();
    // Full screen stays over any page it is not left for first.
    runRemoteAction("exitFullscreen");
    navigate("/settings", { state: { focus: "licensing" } });
  }, [close, navigate]);

  if (!kind) return null;
  const feature = kind in PRO_COPY ? PRO_COPY[kind as keyof typeof PRO_COPY] : null;

  // Left/Right between the two buttons: a TV remote's D-pad, not Tab.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const buttons = Array.from(dialogRef.current?.querySelectorAll("button") ?? []);
    if (buttons.length === 0) return;
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === "ArrowRight" ? 1 : -1;
    const next = buttons[(Math.max(at, 0) + step + buttons.length) % buttons.length];
    next?.focus();
    event.preventDefault();
  };

  return (
    <div className="epg-program-detail-overlay exit-confirm-overlay">
      <div
        ref={dialogRef}
        className="epg-program-detail exit-confirm upgrade-prompt"
        role="alertdialog"
        aria-labelledby="upgrade-prompt-title"
        aria-describedby="upgrade-prompt-body"
        aria-modal="true"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h2 className="epg-detail-title" id="upgrade-prompt-title">
          {feature ? feature.title : "Free viewing time is used up"}
        </h2>
        <div id="upgrade-prompt-body" className="upgrade-prompt__body">
          {feature ? (
            <>
              <p>{feature.body}</p>
              <p className="upgrade-prompt__offer">
                Upgrade to Pro: $1.99 a month, $9.99 a year or $24.99 once.
              </p>
            </>
          ) : (
            <>
              <p>
                You have watched today's free {allowance(kind as PlaybackQuotaKind)} of {WHAT[kind as PlaybackQuotaKind]}. It starts
                again at midnight.
              </p>
              <p className="upgrade-prompt__offer">
                Upgrade to Pro for unlimited watching: $1.99 a month, $9.99 a year or $24.99 once.
              </p>
            </>
          )}
        </div>
        <div className="exit-confirm__actions">
          <button ref={upgradeRef} type="button" className="exit-confirm__leave" onClick={upgrade}>
            Upgrade
          </button>
          <button type="button" className="exit-confirm__stay" onClick={close}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
